/* eslint-disable react/prop-types */
import { MenuItem, TextField, withStyles } from '@material-ui/core';

import { chain } from 'lodash';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { Fragment, PureComponent } from 'react';
import ReactRouterPropTypes from 'react-router-prop-types';
import memoize from 'memoize-one';

import { GET_CLIENTS_FAILURE } from 'modules/clients/types';
import { getClients } from 'modules/clients/clients.actions';
import { getInternalUsers, getRelocationConsultants } from 'modules/common/common.actions';
import { setPageTitle, showToast } from 'modules/layout/layout.actions';
import Log from 'utilities/log';
import auth from 'auth/auth';

import AddNoteDialog from '../../common/addNoteDialog.component';
import DeleteIntakeRecordDialog from './deleteIntakeRecordDialog.component';
import ReassignIntakeDialog from './reassignIntakeDialog.component';
import WebAccessDialog from './webAccessDialog.component';

import {
  ADD_NOTE_FAILURE,
  ASSIGN_INTAKE_RECORD_FAILURE,
  DELETE_INTAKE_RECORD_FAILURE,
  GET_INTAKE_RECORD_FAILURE,
  GET_INTAKE_SUMMARIES_FAILURE,
  REASSIGN_INTAKE_RESULT_MESSAGES,
  REASSIGN_INTAKE_RESULT_SUCCESS,
  SET_INTAKE_RECORD_URGENCY_FAILURE,
} from '../types';
import { INTAKE_RECORD_STATUS_ACTIVE, INTAKE_RECORD_STATUS_CANCELLED } from '../constants';
import { NOTE_MAX_LENGTH } from '../../common/constants';
import { TOAST_MESSAGE_SEVERITY_ERROR } from '../../layout/types';
import {
  addNoteToRecord,
  assignIntakeRecordToRelocationConsultant,
  clearCachedIntakeRecord,
  deleteIntakeRecord,
  getIntakeFormMetadata,
  getIntakeRecord,
  getIntakeSummaries,
  grantWebAccess,
  setIntakeRecordUrgency,
} from '../intake.actions';
import IntakeRecordsGrid from './intakeRecordsGrid.component';
import WebAccessAlterationsDialog from './webAccessAlterationsDialog.component';
import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';

const styles = (theme) => ({
  headerContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
});

class IntakeRecordsView extends PureComponent {
  state = {
    clientId: -1,
    relocationConsultantId: -1,
    intakeRecordStatusId: INTAKE_RECORD_STATUS_ACTIVE,
    addingNoteForRecord: null,
    isAddingNote: false,
    reassigningRecord: null,
    deletingRecord: null,
    isReassigningRecord: false,
    isDeletingRecord: false,
    webAccessRecord: null,
    webAccessSummaryRecord: null,
    showWebAccessModal: false,
    showAlterWebAccessModal: false,
    isLoadingPrimaryData: false,
  };

  async componentDidMount() {
    this.props.setPageTitle('Intake Records');

    this.setState({ isLoadingPrimaryData: true });

    if (!this.props.intakeSummaries || this.props.requiresSummaryRefresh) {
      let { type } = await this.props.getIntakeSummaries();
      if (type === GET_INTAKE_SUMMARIES_FAILURE) {
        this.props.showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }
    }

    if (!this.props.clients) {
      let { type } = await this.props.getClients();
      if (type === GET_CLIENTS_FAILURE) {
        this.props.showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }
    }

    // TODO: Add back auth.userHasRole('Task.Admin') permission checking
    if (!this.props.internalUsers) {
      await this.props.getInternalUsers();
    }

    this.setState({ isLoadingPrimaryData: false });
  }

  handleSummaryChange = async (record) => {
    const { intakeRecordId, intakeRecordUrgency } = record;
    const { type } = await this.props.setIntakeRecordUrgency(intakeRecordId, intakeRecordUrgency);
    if (type === SET_INTAKE_RECORD_URGENCY_FAILURE) {
      this.props.showToast('Failed to set urgency, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    }
  };

  handleSummarySelect = (record) => {
    this.props.history.push(`/intakeRecords/${record.intakeRecordId}`);
  };

  handleChange = (name) => (event) => {
    this.setState({
      [name]: event.target.value,
    });
  };

  handleNoteAdded = async (note) => {
    this.setState({ isAddingNote: true });
    const { type } = await this.props.addNoteToRecord(note, this.state.addingNoteForRecord);
    this.setState({ isAddingNote: false });
    if (type === ADD_NOTE_FAILURE) {
      this.props.showToast('Failed to save note, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } else {
      // kick off data refresh
      this.props.getIntakeSummaries();
      this.props.clearCachedIntakeRecord(this.state.addingNoteForRecord.intakeRecordId);
      this.setState({ addingNoteForRecord: null });
    }
  };

  handleAddNote = (record) => {
    this.setState({ addingNoteForRecord: record });
  };

  handleReassigned = async (relocationConsultant) => {
    this.setState({ isReassigningRecord: true });
    const action = await this.props.assignIntakeRecordToRelocationConsultant(this.state.reassigningRecord, relocationConsultant);
    this.setState({ isReassigningRecord: false });

    // The API rejects stale/missing records and unknown counselors with a 400/404 carrying a message.
    // A 200 always carries a ReassignIntakeCounselorResult, so guard on its result code too.
    const resultCode = String(action.response?.resultCode || '').toLowerCase();
    const succeeded = action.type !== ASSIGN_INTAKE_RECORD_FAILURE &&
      (!resultCode || resultCode === REASSIGN_INTAKE_RESULT_SUCCESS);

    if (!succeeded) {
      const message = action.messages?.[0] ||
        REASSIGN_INTAKE_RESULT_MESSAGES[resultCode] ||
        'Failed to reassign record, please try again';
      this.props.showToast(message, { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    this.setState({ reassigningRecord: null });
    // kick off data refresh
    this.props.getIntakeSummaries();
  };

  handleReassign = (record) => {
    this.setState({ reassigningRecord: record });
  };

  handleDeleted = async () => {
    this.setState({ isDeletingRecord: true });
    const { type } = await this.props.deleteIntakeRecord(this.state.deletingRecord);
    this.setState({ isDeletingRecord: false });
    if (type === DELETE_INTAKE_RECORD_FAILURE) {
      this.props.showToast('Failed to cancel intake, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } else {
      this.setState({ deletingRecord: null });
      // kick off data refresh
      this.props.getIntakeSummaries();
    }
  };

  handleDelete = (record) => {
    this.setState({ deletingRecord: record });
  };

  filterSummaries = memoize((intakeSummaries, clientId, relocationConsultantId, intakeRecordStatusId) => {
    return intakeSummaries.filter((summary) => {
      return (clientId === -1 || summary.clientRecordId === clientId) &&
        (relocationConsultantId === -1 || summary.relocationCounselorId === relocationConsultantId) &&
        // A summary without a status came from an API that only returns active records
        (intakeRecordStatusId === -1 || (summary.intakeRecordStatusId ?? INTAKE_RECORD_STATUS_ACTIVE) === intakeRecordStatusId);
    });
  });


  getClientList = memoize((clients) => {
    if (!clients) {
      return [];
    }
    return chain(clients)
      .values()
      .map(client => ({
        ...client,
        id: client.clientRecordId || client.id
      }))
      .uniqWith(clients, (a, b) => a.shortName === b.shortName)
      .value()
      .sort((a, b) => String(a.shortName).localeCompare(b.shortName))
      .filter((instance) => instance.shortName);
  });

  getRelocationConsultantList = memoize((relocationConsultants) => {
    if (!relocationConsultants) {
      return [];
    }
    return chain(relocationConsultants)
      .values()
      .uniqWith(relocationConsultants, (a, b) => a.fullName === b.fullName)
      .value()
      .sort((a, b) => String(a.fullName).localeCompare(b.fullName))
      .filter((instance) => instance.fullName);
  });

  getInternalUsersList = memoize((internalUsers) => {
    if (!internalUsers) {
      return [];
    }
    return internalUsers
      .map(user => ({
        ...user,
        id: user.internalUserId,
        fullName: [user.firstName, user.lastName].map(name => (name || '').trim()).filter(Boolean).join(' ')
      }))
      .filter(user => user.fullName)
      .sort((a, b) => a.fullName.localeCompare(b.fullName, undefined, { sensitivity: 'base' }));
  });

  webAccessClicked = async (record) => {
    this.setState({ showWebAccessModal: true, webAccessSummaryRecord: record });
    let resp = await this.props.getIntakeRecord(record.intakeRecordId);
    if (resp.type === GET_INTAKE_RECORD_FAILURE) {
      this.props.showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }
    let intakeRecord = resp.response;
    this.setState({ webAccessRecord: intakeRecord });
  }

  alterWebAccessClicked = async (record) => {
    this.setState({ showAlterWebAccessModal: true, webAccessSummaryRecord: record });
    let resp = await this.props.getIntakeRecord(record.intakeRecordId);
    if (resp.type === GET_INTAKE_RECORD_FAILURE) {
      this.props.showToast('Failed to retrieve data, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }
    let intakeRecord = resp.response;
    this.setState({ webAccessRecord: intakeRecord });
  }

  render() {
    Log.trace('RENDER', 'IntakeRecordsView');
    const { clientId, relocationConsultantId, intakeRecordStatusId, addingNoteForRecord, isAddingNote, reassigningRecord, isReassigningRecord, deletingRecord, isDeletingRecord, showWebAccessModal, showAlterWebAccessModal, webAccessRecord, webAccessSummaryRecord } = this.state;
    const { classes, clients, internalUsers, intakeSummaries, isLoading } = this.props;
    const intakeRecords = this.filterSummaries(intakeSummaries || [], clientId, relocationConsultantId, intakeRecordStatusId);
    const canDeleteRecords = auth.userHasPermission('intake:fullAccess');
    const sortedClients = this.getClientList(clients);
    let sortedInternalUsers = [];
    let assignee = null;

    // TODO: Add back auth.userHasRole('Task.Admin') permission checking
    //if (auth.userHasRole('Task.Admin')) {
      sortedInternalUsers = this.getInternalUsersList(internalUsers);
      assignee = reassigningRecord ? sortedInternalUsers.find((user) => user.internalUserId === reassigningRecord.relocationCounselorId) : null;
    //}

    if (isLoading || this.state.isLoadingPrimaryData) return <FullscreenSpinner/>;

    return (
      <Fragment>
        <div className={classes.headerContainer}>
          <TextField
            select
            label="Clients"
            className={classes.textField}
            value={clientId}
            onChange={this.handleChange('clientId')}
            SelectProps={{
              autoWidth: true,
            }}
            variant="outlined"
          >
            <MenuItem value={-1}>
              <em>All Clients</em>
            </MenuItem>
            {sortedClients.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.shortName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Consultants"
            className={classes.textField}
            value={relocationConsultantId}
            onChange={this.handleChange('relocationConsultantId')}
            SelectProps={{
              autoWidth: true,
            }}
            variant="outlined"
          >
            <MenuItem value={-1}>
              <em>All Consultants</em>
            </MenuItem>
            {sortedInternalUsers.map((option) => (
              <MenuItem key={option.internalUserId} value={option.internalUserId}>
                {option.fullName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            className={classes.textField}
            value={intakeRecordStatusId}
            onChange={this.handleChange('intakeRecordStatusId')}
            SelectProps={{
              autoWidth: true,
            }}
            variant="outlined"
          >
            <MenuItem value={-1}>
              <em>All</em>
            </MenuItem>
            <MenuItem value={INTAKE_RECORD_STATUS_ACTIVE}>Active</MenuItem>
            <MenuItem value={INTAKE_RECORD_STATUS_CANCELLED}>Cancelled</MenuItem>
          </TextField>
        </div>
        <div className="px-2">
          <IntakeRecordsGrid
            intakeRecords={intakeRecords}
            onChange={this.handleSummaryChange}
            onSelect={this.handleSummarySelect}
            onAddNote={this.handleAddNote}
            onReassign={this.handleReassign}
            onDelete={this.handleDelete}
            canDelete={canDeleteRecords}
            onGrantWebAccess={this.webAccessClicked}
            onAlterWebAccess={this.alterWebAccessClicked}
            isLoading={isLoading}
            internalUsers={internalUsers}
            clients={sortedClients}
          />
        </div>
        {addingNoteForRecord && // forces note to clear each time, allows for retrying save if it fails
          <AddNoteDialog
            disabled={isAddingNote}
            open={true}
            onDiscard={() => this.setState({ addingNoteForRecord: null })}
            onSubmit={this.handleNoteAdded}
            maxLength={NOTE_MAX_LENGTH}
          />
        }
        {reassigningRecord &&
          <ReassignIntakeDialog
            disabled={isReassigningRecord}
            open={true}
            onCancel={() => this.setState({ reassigningRecord: null })}
            onSubmit={this.handleReassigned}
            assignee={assignee}
            relocationConsultants={sortedInternalUsers}
          />
        }
        {deletingRecord &&
          <DeleteIntakeRecordDialog
            disabled={isDeletingRecord}
            open={true}
            onCancel={() => this.setState({ deletingRecord: null })}
            onSubmit={this.handleDeleted}
          />
        }
        {showWebAccessModal && 
          <WebAccessDialog
            disabled={isDeletingRecord}
            open={true}
            onCancel={() => this.setState({ showWebAccessModal: false, webAccessRecord: null, webAccessSummaryRecord: null })}
            onSubmit={this.handleDeleted}
            record={webAccessRecord}
            summaryRecord={webAccessSummaryRecord}
          />
        }
        {showAlterWebAccessModal && 
          <WebAccessAlterationsDialog
            disabled={isDeletingRecord}
            open={true}
            onCancel={() => this.setState({ showAlterWebAccessModal: false, webAccessRecord: null, webAccessSummaryRecord: null })}
            onSubmit={this.handleDeleted}
            record={webAccessRecord}
            summaryRecord={webAccessSummaryRecord}
          />
        }
      </Fragment>
    );
  }
}

IntakeRecordsView.propTypes = {
  setPageTitle: PropTypes.func.isRequired,
  getIntakeSummaries: PropTypes.func.isRequired,
  setIntakeRecordUrgency: PropTypes.func.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  intakeSummaries: PropTypes.array,
  classes: PropTypes.object.isRequired,
  clients: PropTypes.object,
  relocationConsultants: PropTypes.object,
  internalUsers: PropTypes.array,
  getClients: PropTypes.func.isRequired,
  getRelocationConsultants: PropTypes.func.isRequired,
  getInternalUsers: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  showToast: PropTypes.func.isRequired,
  requiresSummaryRefresh: PropTypes.bool.isRequired,
  getIntakeFormMetadata: PropTypes.func.isRequired,
  metadata: PropTypes.object,
  addNoteToRecord: PropTypes.func.isRequired,
  assignIntakeRecordToRelocationConsultant: PropTypes.func.isRequired,
  deleteIntakeRecord: PropTypes.func.isRequired,
  clearCachedIntakeRecord: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const {
    intake: { isLoading, intakeSummaries, requiresSummaryRefresh, metadata },
    common: { relocationConsultants, internalUsers },
    clients: { unmappedClients },
  } = state;
  return { isLoading, intakeSummaries, clients: unmappedClients, relocationConsultants, internalUsers, requiresSummaryRefresh, metadata };
};

export default compose(
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, {
    setPageTitle,
    getIntakeSummaries,
    getIntakeRecord,
    setIntakeRecordUrgency,
    getClients,
    getRelocationConsultants,
    showToast,
    getIntakeFormMetadata,
    addNoteToRecord,
    assignIntakeRecordToRelocationConsultant,
    deleteIntakeRecord,
    grantWebAccess,
    clearCachedIntakeRecord,
    getInternalUsers
  }),
)(IntakeRecordsView);
